<template>
  <div :class="fieldClass">
    <el-input
      :value="value"
      @input="handleInput"
      :type="inputType"
      :placeholder="translatePlaceholder(field.placeholder)"
      :clearable="getProperty('clearable', false)"
      :disabled="field.isDisabled"
      :readonly="field.isReadonly"
      :maxlength="getProperty('maxlength')"
      :minlength="getProperty('minlength')"
      :step="getProperty('step')"
      :min="getProperty('min')"
      :max="getProperty('max')"
      :size="fieldSize"
    >
      <template v-if="getProperty('prefix')" slot="prepend">
        {{ getProperty('prefix') }}
      </template>
      <template v-if="getProperty('suffix')" slot="append">
        {{ getProperty('suffix') }}
      </template>
    </el-input>
    
    <!-- Aide contextuelle -->
    <div v-if="field.helpText" class="field-help-text">
      <small class="text-muted">{{ $t(field.helpText) }}</small>
    </div>
  </div>
</template>

<script>
import placeholderTranslation from '@/mixins/placeholderTranslation'

export default {
  name: 'GenericFieldInput',
  mixins: [placeholderTranslation],
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number],
      default: ''
    }
  },
  
  computed: {
    inputType() {
      // Détermine le type d'input selon les propriétés du champ
      const fieldType = this.getProperty('inputType', 'text')
      return ['text', 'number', 'email', 'tel', 'url', 'password'].includes(fieldType) 
        ? fieldType 
        : 'text'
    },
    fieldClass() {
      return {
        'search-field': true,
        'search-field-input': true,
        'search-field-required': this.field.isRequired,
        'search-field-disabled': this.field.isDisabled
      }
    },

    // Taille du champ - undefined = taille par défaut Element UI (comme l'original)
    fieldSize() {
      const size = this.getProperty('size')
      return size || undefined
    }
  },
  methods: {
    handleInput(inputValue) {
      let processedValue = inputValue

      // Traitement selon le type
      if (this.inputType === 'number' && processedValue !== '') {
        processedValue = Number(processedValue)
        
        // Appliquer min/max si définis
        const min = this.getProperty('min')
        const max = this.getProperty('max')
        
        if (min !== undefined && processedValue < min) {
          processedValue = min
        }
        if (max !== undefined && processedValue > max) {
          processedValue = max
        }
      }

      this.$emit('input', processedValue)
      this.$emit('change', { field: this.field.name, value: processedValue })
    },

    getProperty(propertyName, defaultValue = undefined) {
      // Récupère une propriété depuis la configuration du champ
      if (!this.field.properties || !Array.isArray(this.field.properties)) {
        return defaultValue
      }

      const property = this.field.properties.find(p => p.name === propertyName)
      if (!property) {
        return defaultValue
      }

      // Conversion selon le type
      switch (property.type) {
        case 'boolean':
          return property.value === 'true' || property.value === true
        case 'number':
        case 'integer':
          return property.value ? Number(property.value) : defaultValue
        case 'string':
        default:
          return property.value || defaultValue
      }
    }
  }
}
</script>

<style scoped>
.search-field {
  margin-bottom: 16px;
}

.search-field-required .el-form-item__label::after {
  content: ' *';
  color: #f56c6c;
}

.search-field-disabled {
  opacity: 0.6;
}

.field-help-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.text-muted {
  color: #909399;
}

/* Styles pour les différents types d'input */
.search-field-input .el-input .el-input__inner {
  font-size: 14px;
  height: 36px;
  line-height: 36px;
}

.search-field-input .el-input--small .el-input__inner {
  height: 32px;
  line-height: 32px;
}

/* Styles pour les groupes d'input (avec prefix/suffix) */
.search-field-input .el-input-group__prepend,
.search-field-input .el-input-group__append {
  background-color: #f5f7fa;
  border-color: #dcdfe6;
  color: #909399;
  font-size: 14px;
  padding: 0 12px;
}

.search-field-input .el-input-group__prepend {
  border-right: 0;
}

.search-field-input .el-input-group__append {
  border-left: 0;
}
</style>
